import {animate} from 'motion';

/**
 * Set up hover animations for a collection of links
 * @param {NodeList} links - Collection of links to animate
 * @param {Object} options - Animation options
 * @param {string} options.circleSize - Size of the circle on hover (e.g., '26px')
 */
export function setupLinkHoverAnimations(links, options = {}) {
  const {
    circleSize = '26px'
  } = options;

  if (links.length === 0) return;

  // Store animation controls for each link to properly cancel them
  const animationControls = new Map();

  // Initialize all links to opacity 1
  links.forEach(link => {
    link.style.opacity = '1';
  });

  // Add a mouseleave handler to the parent container as backup
  if (links.length > 0 && links[0].parentElement) {
    links[0].parentElement.addEventListener('mouseleave', () => {
      // Reset all links to full opacity when leaving the container
      links.forEach(link => {
        link.style.opacity = '1';
      });
    });
  }

  // Set up hover animations for each link
  links.forEach((link, index) => {
    // Check if this link has a circle (via has-circle class)
    const hasCircle = link.classList.contains('has-circle');

    // Get the circle element inside the link (if applicable)
    const circle = hasCircle ? link.querySelector('span > span.absolute') : null;
    // Get the chevron SVG element
    const chevron = hasCircle ? link.querySelector('span > svg') : null;
    // Get the parent span that contains both circle and chevron
    const containerSpan = hasCircle && circle ? circle.parentElement : null;

    // Skip circle animation setup if no circle found when expected
    if (hasCircle && !circle) {
      console.warn('Circle element not found in link with has-circle class', link);
    }

    // Store the original chevron color class for later restoration
    let originalChevronColorClass = '';
    if (hasCircle && chevron) {
      // Find the text color class (starts with 'text-')
      Array.from(chevron.classList).forEach(className => {
        if (className.startsWith('text-')) {
          originalChevronColorClass = className;
        }
      });
    }

    // Initialize circle and chevron styles
    if (hasCircle && circle && chevron && containerSpan) {
      // Ensure the circle starts with zero width/height
      circle.style.width = '0';
      circle.style.height = '0';

      // Ensure proper CSS transition for smooth animation
      circle.style.transition = 'width 0.2s ease-out, height 0.2s ease-out';

      // The circle has absolute positioning with top-1/2 left-1/2 and transform from the markup
      // No need to add additional positioning

      // Make sure the chevron is properly positioned with z-index
      chevron.style.zIndex = '10';
    }

    // Hover enter animation
    link.addEventListener('mouseenter', () => {
      // Cancel any ongoing animations
      if (animationControls.has('global')) {
        animationControls.get('global').stop();
      }

      // Create a new animation for other links
      const otherLinksAnimation = animate(
        Array.from(links).filter(item => item !== link),
        {opacity: 0.5},
        {
          duration: 0.2, // Fast animation
          easing: 'ease-out'
        }
      );

      // Create a new animation for the current link
      const currentLinkAnimation = animate(
        link,
        {opacity: 1},
        {duration: 0.1}
      );

      // Store the animation controls for cleanup
      animationControls.set('global', {
        stop: () => {
          otherLinksAnimation.stop();
          currentLinkAnimation.stop();
        }
      });

      // Animate the circle expansion (if applicable)
      if (hasCircle && circle) {
        // Set the final width and height with direct style manipulation
        circle.style.width = circleSize;
        circle.style.height = circleSize;
      }

      // Change chevron color to white when hovered
      if (hasCircle && chevron) {
        // Remove the original color class
        if (originalChevronColorClass) {
          chevron.classList.remove(originalChevronColorClass);
        }
        chevron.classList.add('text-white');
      }
    });

    // Hover exit animation
    link.addEventListener('mouseleave', () => {
      // Cancel any ongoing animations
      if (animationControls.has('global')) {
        animationControls.get('global').stop();
      }

      // Create a new animation for resetting opacity
      const resetAnimation = animate(
        links,
        {opacity: 1},
        {
          duration: 0.2, // Fast animation
          easing: 'ease-out'
        }
      );

      // Store the animation control
      animationControls.set('global', resetAnimation);

      // Animate the circle back to its initial state (if applicable)
      if (hasCircle && circle) {
        // Set the width and height back to 0
        circle.style.width = '0';
        circle.style.height = '0';
      }

      // Change chevron color back to original color when not hovered
      if (hasCircle && chevron) {
        chevron.classList.remove('text-white');
        // Restore the original color class
        if (originalChevronColorClass) {
          chevron.classList.add(originalChevronColorClass);
        } else {
          // Fallback to teal if no original color was found
          chevron.classList.add('text-[#01484C]');
        }
      }
    });
  });
}

export default setupLinkHoverAnimations;
